import { WEBRTC_PROTOCOL_CONFIG } from "../config/protocol_config";

export type CameraTrackSlot = keyof typeof WEBRTC_PROTOCOL_CONFIG.cameraElementIds;

export const CAMERA_TRACK_SLOTS = Object.freeze([
  ...WEBRTC_PROTOCOL_CONFIG.cameraSlotOrder,
]) as readonly CameraTrackSlot[];

export const CAMERA_ELEMENT_IDS: Record<CameraTrackSlot, string> = {
  ...WEBRTC_PROTOCOL_CONFIG.cameraElementIds,
};

// Internal camera slot -> negotiated WebRTC MID.
// Keys are the app's stable slot ids; values are the mids written into SDP.
export const DEFAULT_CAMERA_TRACK_MIDS: Record<CameraTrackSlot, string> = {
  ...WEBRTC_PROTOCOL_CONFIG.cameraTrackMids,
};

// Internal audio role -> negotiated WebRTC MID.
export const DEFAULT_AUDIO_TRACK_MIDS = {
  ...WEBRTC_PROTOCOL_CONFIG.audioTrackMids,
} as const;

export type WebRtcMidConfig = {
  video?: Partial<Record<CameraTrackSlot, string>>;
  audio?: {
    vehicleMic?: string;
    vehicle_mic?: string;
    operatorMic?: string;
    operator_mic?: string;
  };
  aliases?: Record<string, CameraTrackSlot>;
};

export type ResolvedWebRtcMidConfig = {
  video: Record<CameraTrackSlot, string>;
  audio: {
    vehicleMic: string;
    operatorMic: string;
  };
  aliases: Record<string, CameraTrackSlot>;
};

// Incoming MID alias -> internal camera slot.
// Use this only to accept legacy peer mids; offer generation does not read from here.
const DEFAULT_CAMERA_TRACK_ALIASES: Record<string, CameraTrackSlot> = {
  ...(WEBRTC_PROTOCOL_CONFIG.cameraTrackAliases as Record<string, CameraTrackSlot>),
};

export const DEFAULT_WEBRTC_MID_CONFIG: ResolvedWebRtcMidConfig = {
  video: {
    ...DEFAULT_CAMERA_TRACK_MIDS,
  },
  audio: {
    vehicleMic: DEFAULT_AUDIO_TRACK_MIDS.vehicleMic,
    operatorMic: DEFAULT_AUDIO_TRACK_MIDS.operatorMic,
  },
  aliases: {
    ...DEFAULT_CAMERA_TRACK_ALIASES,
  },
};

function isMidConfigObject(value: any): boolean {
  if (!value || typeof value !== "object") {
    return false;
  }

  return Boolean(
    value.video ||
    value.audio ||
    value.aliases ||
    value.webrtcMidConfig ||
    value.webrtc_mid_config,
  );
}

function getMidConfigSource(config: any): WebRtcMidConfig | null {
  if (!isMidConfigObject(config)) {
    return null;
  }

  if (config.webrtcMidConfig || config.webrtc_mid_config) {
    return config.webrtcMidConfig || config.webrtc_mid_config;
  }

  return config;
}

export function resolveWebRtcMidConfig(config?: any): ResolvedWebRtcMidConfig {
  const source = getMidConfigSource(config);
  if (!source) {
    return DEFAULT_WEBRTC_MID_CONFIG;
  }

  const audioSource = source.audio || {};

  return {
    video: {
      ...DEFAULT_CAMERA_TRACK_MIDS,
      ...(source.video || {}),
    },
    audio: {
      vehicleMic:
        audioSource.vehicleMic ||
        audioSource.vehicle_mic ||
        DEFAULT_AUDIO_TRACK_MIDS.vehicleMic,
      operatorMic:
        audioSource.operatorMic ||
        audioSource.operator_mic ||
        DEFAULT_AUDIO_TRACK_MIDS.operatorMic,
    },
    aliases: {
      ...DEFAULT_CAMERA_TRACK_ALIASES,
      ...(source.aliases || {}),
    },
  };
}

function buildCameraTrackAliasMap(
  config?: any,
): Record<string, CameraTrackSlot> {
  const resolved = resolveWebRtcMidConfig(config);
  const aliasMap: Record<string, CameraTrackSlot> = {
    ...resolved.aliases,
  };

  CAMERA_TRACK_SLOTS.forEach((slot) => {
    aliasMap[slot] = slot;
    aliasMap[resolved.video[slot]] = slot;
  });

  return aliasMap;
}

export function getConfiguredCameraTrackMid(
  slot: CameraTrackSlot,
  config?: any,
): string {
  return resolveWebRtcMidConfig(config).video[slot];
}

export function getConfiguredVehicleAudioMid(config?: any): string {
  return resolveWebRtcMidConfig(config).audio.vehicleMic;
}

export function getConfiguredOperatorAudioMid(config?: any): string {
  return resolveWebRtcMidConfig(config).audio.operatorMic;
}

export function isConfiguredVehicleAudioMid(
  mid: string | null | undefined,
  config?: any,
): boolean {
  return Boolean(mid) && mid === getConfiguredVehicleAudioMid(config);
}

export function getConfiguredOfferMediaPlan(
  config?: any,
): Array<{ kind: "video" | "audio"; mid: string }> {
  const resolved = resolveWebRtcMidConfig(config);

  return [
    ...CAMERA_TRACK_SLOTS.map((slot) => ({
      kind: "video" as const,
      mid: resolved.video[slot],
    })),
    {
      kind: "audio" as const,
      mid: resolved.audio.vehicleMic,
    },
    {
      kind: "audio" as const,
      mid: resolved.audio.operatorMic,
    },
  ];
}

export function resolveCameraTrackSlotByMediaSectionIndex(
  index: number,
  config?: any,
): CameraTrackSlot | null {
  if (!Number.isInteger(index) || index < 0) {
    return null;
  }

  const mediaPlan = getConfiguredOfferMediaPlan(config);
  const mediaSection = mediaPlan[index];
  if (!mediaSection || mediaSection.kind !== "video") {
    return null;
  }

  return resolveCameraTrackSlot(mediaSection.mid, config);
}

export function resolveCameraTrackSlot(
  mid: string | null | undefined,
  config?: any,
): CameraTrackSlot | null {
  if (!mid) {
    return null;
  }

  return buildCameraTrackAliasMap(config)[mid] || null;
}
